/**
 * Social Share API Service
 * Handles all API calls for article sharing
 */

import { apiClient } from '@/app/api/client';
import { AxiosError } from 'axios';

// Interfaces
interface ShareTextResponse {
  text: string;
  platform: string;
  characterCount: number;
  suggestions?: string[];
  metadata?: { title?: string; body?: string };
}

interface ShareResponse {
  success: boolean;
  platform: string;
  externalUrl?: string;
  message: string;
}

interface ShareHistoryItem {
  id: string;
  platform: string;
  status: 'success' | 'failed';
  externalUrl?: string;
  errorMessage?: string;
  createdAt: string;
}

class SocialShareApiService {
  private handleError(error: unknown): never {
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('An unexpected API error occurred');
  }

  async generateShareText(
    articleId: string,
    data: { platform: string; connectionId?: string }
  ): Promise<ShareTextResponse> {
    try {
      const response = await apiClient.post<ShareTextResponse>(`/api/articles/${articleId}/share-text`, data);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async executeShare(
    articleId: string,
    data: { connectionId: string; platform: string; message: string; metadata?: Record<string, unknown> }
  ): Promise<ShareResponse> {
    try {
      const response = await apiClient.post<ShareResponse>(`/api/articles/${articleId}/share`, data);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getShareHistory(articleId: string): Promise<ShareHistoryItem[]> {
    try {
      const response = await apiClient.get<ShareHistoryItem[]>(`/api/articles/${articleId}/share-history`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async generateManualShareContent(
    articleId: string,
    platform: string,
    direction?: string
  ): Promise<ShareTextResponse> {
    try {
      const response = await apiClient.post<ShareTextResponse>(`/api/articles/${articleId}/share-text`, { platform, direction });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }
}

export const socialShareApi = new SocialShareApiService();
export default socialShareApi;
